import { z } from "zod";
//#region src/rings.ts
/**
* Ring — the maturity tier of an app version.
*
* Single source of truth is CP `release.channels` (4-row seed). This module
* defines the canonical TypeScript type, the ordered tuple, the ordinal
* comparison map, and the minimum-ring policy predicate. Every consumer
* (CP, DP, operator, CLI, UI) imports from here to avoid string-vocabulary
* fracture.
*/
/** Ordered tuple, stable → edge. */
const RINGS = [
	"stable",
	"beta",
	"alpha",
	"edge"
];
/**
* Ordinal of each ring. Lower is more stable. Used to compare a candidate
* ring against a minimum-ring policy floor.
*/
const RING_ORDINAL = {
	stable: 0,
	beta: 1,
	alpha: 2,
	edge: 3
};
/**
* True when `candidate` is at most as edge-leaning as `floor` — i.e. the
* candidate's ring is at or below the minimum-ring policy floor.
*
* `floor = 'beta'` admits `'stable'` and `'beta'` but rejects `'alpha'` and
* `'edge'`. The minimum-ring policy semantics: "no user gets a ring more
* adventurous than X."
*/
function ringMeetsMinimum(candidate, floor) {
	return RING_ORDINAL[candidate] <= RING_ORDINAL[floor];
}
/** Runtime type-guard — true iff `v` is one of the four ring strings. */
function isRing(v) {
	return typeof v === "string" && RINGS.includes(v);
}
/**
* The zod twin of {@link Ring}, built from the same tuple so the two can never disagree. Added
* for `@zion/sdk-dp-admin-contracts` (ruling R-27 of the admin-app extraction), which had grown
* its own copy; every consumer that validates a ring at a boundary imports this one.
*/
const ringSchema = z.enum([
	"stable",
	"beta",
	"alpha",
	"edge"
]);
//#endregion
export { RINGS, RING_ORDINAL, isRing, ringMeetsMinimum, ringSchema };

//# sourceMappingURL=rings.js.map